import { useState, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import { SUPABASE_URL, SUPABASE_KEY } from '../lib/constants.js';
import { getValidToken } from '../lib/supabase.js';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const MAX_PRE_COMPRESSION_BYTES = 10 * 1024 * 1024;   // 10 MB hard limit
const BUCKET                    = 'product-images';
// Sent on every upload. Without it Supabase stores `no-cache` on the object.
export const CACHE_CONTROL      = 'public, max-age=31536000, immutable';

// ─── FILENAME SANITISER ───────────────────────────────────────────────────────
// Produces a URL-safe, collision-resistant filename: "product-name-1715000000000.webp"
function sanitizeFilename(rawName) {
  const slug = (rawName || 'product')
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9\u0600-\u06ff-]/g, '')   // keep ASCII alphanum + Arabic + hyphens
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'product';
  return `${slug}-${Date.now()}.webp`;
}

// ─── HOOK ─────────────────────────────────────────────────────────────────────
// status: 'idle' | 'compressing' | 'uploading' | 'done' | 'error'
// progress: 0–100 (combined compressing + uploading)
export function useImageUpload() {
  const [status,   setStatus]   = useState('idle');
  const [progress, setProgress] = useState(0);
  const [url,      setUrl]      = useState('');
  const [error,    setError]    = useState('');

  const reset = useCallback(() => {
    setStatus('idle');
    setProgress(0);
    setUrl('');
    setError('');
  }, []);

  // upload(file, nameHint, opts) → Promise<string | null>
  // Compresses → converts to WebP → uploads → returns public URL
  // opts.maxWidthOrHeight / opts.initialQuality let callers pick a budget:
  // product cards are small on screen (1400px is generous), while a full-bleed
  // hero needs more width but tolerates heavier compression.
  const upload = useCallback(async (file, nameHint = 'product', opts = {}) => {
    if (!file) return null;
    const maxWidthOrHeight = opts.maxWidthOrHeight ?? 1400;
    const initialQuality   = opts.initialQuality   ?? 0.8;

    // ── Pre-flight size guard ────────────────────────────────────────────────
    if (file.size > MAX_PRE_COMPRESSION_BYTES) {
      const mb  = (file.size / 1024 / 1024).toFixed(1);
      const msg = `File too large (${mb} MB). Max 10 MB allowed.`;
      setError(msg);
      setStatus('error');
      return null;
    }

    setError('');
    setStatus('compressing');
    setProgress(5);

    // ── Step 1: Compress + convert to WebP ──────────────────────────────────
    let compressedBlob;
    try {
      compressedBlob = await imageCompression(file, {
        maxWidthOrHeight,
        initialQuality,
        fileType:         'image/webp',
        useWebWorker:     false,            // avoids CSP issues in some hosts
        onProgress: (p) => {
          setProgress(5 + Math.floor(p * 0.35));  // maps 0-100 → 5-40%
        },
      });
    } catch (err) {
      // Fallback: if browser doesn't support WebP encoding via canvas,
      // try again without forcing fileType (delivers JPEG/PNG instead)
      try {
        compressedBlob = await imageCompression(file, {
          maxWidthOrHeight,
          initialQuality,
          useWebWorker:     false,
        });
      } catch (err2) {
        setError('Image compression failed: ' + (err2.message || String(err2)));
        setStatus('error');
        return null;
      }
    }

    setStatus('uploading');
    setProgress(42);

    // ── Step 2: Upload via XHR for real progress events ──────────────────────
    const filename  = sanitizeFilename(nameHint);
    const mimeType  = compressedBlob.type || 'image/webp';
    const token     = (await getValidToken()) || SUPABASE_KEY;
    const uploadURL = `${SUPABASE_URL}/storage/v1/object/${BUCKET}/${filename}`;

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', uploadURL, true);
      xhr.setRequestHeader('apikey',         SUPABASE_KEY);
      xhr.setRequestHeader('Authorization',  `Bearer ${token}`);
      xhr.setRequestHeader('Content-Type',   mimeType);
      xhr.setRequestHeader('x-upsert',       'true');
      // Supabase defaults an object to `no-cache` when this header is absent,
      // which forces every browser to revalidate every image on every page
      // view. sanitizeFilename() stamps Date.now() into the name, so a URL
      // never points at different bytes — safe to cache immutably for a year.
      xhr.setRequestHeader('cache-control',  CACHE_CONTROL);
      xhr.timeout = 60_000;   // 60-second network timeout

      xhr.upload.addEventListener('progress', (evt) => {
        if (evt.lengthComputable) {
          // 42% → 97% for upload phase
          const pct = 42 + Math.floor((evt.loaded / evt.total) * 55);
          setProgress(pct);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/${filename}`;
          setUrl(publicUrl);
          setProgress(100);
          setStatus('done');
          resolve(publicUrl);
        } else {
          let msg = `Upload failed (HTTP ${xhr.status})`;
          try {
            const body = JSON.parse(xhr.responseText);
            if (body?.error) msg = body.error;
            else if (body?.message) msg = body.message;
          } catch {}
          setError(msg);
          setStatus('error');
          resolve(null);
        }
      });

      xhr.addEventListener('error', () => {
        setError('Network error — check your connection and try again.');
        setStatus('error');
        resolve(null);
      });

      xhr.addEventListener('timeout', () => {
        setError('Upload timed out (60 s). Check your connection and try again.');
        setStatus('error');
        resolve(null);
      });

      xhr.send(compressedBlob);
    });
  }, []);

  return { upload, status, progress, url, error, reset };
}
